/**
 * 따라하기 가이드 — 큐로 신기능(AI 학습·데이터·파이썬·리포트)을 단계별로 안내.
 * 2026-07-24. 대표 요청 "튜토리얼 콘텐츠".
 *
 * ⭐ 왜 스크래치 표준 덱을 안 쓰는가(조사 결과):
 *    표준 덱은 스텝마다 **GIF/동영상 자산**이 필수(en-steps.js 등 언어별 이미지 세트).
 *    큐로 신기능 튜토리얼 4개 = GIF 15~20장 = 디자인 작업 대기 → 지금 진행 불가.
 *    → **이미지 0장**으로 되는 방식: 텍스트 단계 + "블록 놓아주기" 같은 실제 동작(action).
 *      말로만 설명하지 않고 **눌러서 진짜로 되게** 한다(중등1~2엔 이게 더 잘 통함).
 *
 * 구조: {key, icon, color, steps:[{t(텍스트), action?}]}
 *   action — 'blocks'(블록 자동 배치) | 'tab:ai' | 'tab:python' | 'ext'(확장 갤러리) | 'report'(리포트 열기)
 */

export const GUIDES = [
    {
        key: 'ai',
        icon: 'brain',
        color: '#14c8a6',
        steps: [
            {t: 'aiS1'},                       // AI 학습 탭이 뭔지
            {t: 'aiS2', action: 'tab:ai'},     // 탭으로 데려가기
            {t: 'aiS3'},                       // 클래스 만들고 사진 찍기
            {t: 'aiS4'},                       // 학습시키기
            {t: 'aiS5', action: 'tab:code'}    // 코드에서 쓰기
        ]
    },
    {
        key: 'data',
        icon: 'chart',
        color: '#ff8c1a',
        steps: [
            {t: 'dataS1'},
            {t: 'dataS2', action: 'ext'},      // 확장 갤러리 열어 데이터 차트 추가
            {t: 'dataS3'},
            {t: 'dataS4'}
        ]
    },
    {
        key: 'python',
        icon: 'code',
        color: '#306998',
        steps: [
            {t: 'pyS1'},
            {t: 'pyS2', action: 'tab:python'},
            {t: 'pyS3'},
            {t: 'pyS4'}
        ]
    },
    {
        key: 'report',
        icon: 'doc',
        color: '#4c97ff',
        steps: [
            {t: 'rpS1'},
            {t: 'rpS2', action: 'report'},
            {t: 'rpS3'}
        ]
    }
];

/** 가이드 문구 (11개 언어). 제목/단계 텍스트. */
export const GUIDE_L = {
    en: {
        open: 'Guides', title: 'Step-by-step guides', sub: 'Follow along — buttons actually do the thing',
        next: 'Next', prev: 'Back', done: 'Done', close: 'Close', step: 'Step',
        g: {ai: 'Train your own AI', data: 'Charts and data', python: 'Code with Python', report: 'Make a learning report'},
        s: {
            aiS1: 'The AI Learning tab lets you teach the computer to recognize things with your webcam.',
            aiS2: 'Open the AI Learning tab.',
            aiS3: 'Make a class (like "apple"), then take about 20 photos with the webcam.',
            aiS4: 'Make one more class, then press Train. Wait until it says done.',
            aiS5: 'Back in Code, use the AI blocks to react to what it recognizes.'
        }
    },
    ko: {
        open: '따라하기', title: '따라하기 가이드', sub: '버튼을 누르면 실제로 그 일이 일어나요',
        next: '다음', prev: '이전', done: '완료', close: '닫기', step: '단계',
        g: {ai: '나만의 AI 학습시키기', data: '데이터로 차트 만들기', python: '파이썬으로 코딩하기', report: '학습 리포트 만들기'},
        s: {
            aiS1: 'AI 학습 탭에서는 웹캠으로 컴퓨터에게 사물을 알아보도록 가르칠 수 있어요.',
            aiS2: 'AI 학습 탭을 열어볼까요?',
            aiS3: '클래스를 하나 만들고(예: "사과") 웹캠으로 사진을 20장쯤 찍어요.',
            aiS4: '클래스를 하나 더 만든 다음 학습 버튼을 눌러요. 완료될 때까지 기다려요.',
            aiS5: '코드 탭으로 돌아가서 AI 블록으로 알아본 결과에 반응하게 만들어요.',
            dataS1: '데이터 차트로 숫자를 모아 그래프를 그릴 수 있어요.',
            dataS2: '확장 갤러리에서 "데이터 차트"를 추가해요.',
            dataS3: '"데이터 추가하기" 블록으로 항목과 값을 넣어요. (예: 사과 5)',
            dataS4: '"차트 보이기" 블록을 실행하면 무대에 그래프가 그려져요.',
            pyS1: '파이썬 탭에서는 진짜 파이썬 코드를 브라우저에서 바로 실행할 수 있어요.',
            pyS2: '파이썬 탭을 열어볼까요?',
            pyS3: 'print("안녕") 을 써보고 ▶ Run 을 눌러요.',
            pyS4: 'Python 3 버튼을 누르면 pandas 같은 라이브러리도 쓸 수 있어요.',
            rpS1: '오늘 한 활동을 리포트로 저장할 수 있어요. 세특·포트폴리오에 좋아요.',
            rpS2: '상단바의 리포트 버튼을 눌러볼까요?',
            rpS3: '이름을 넣고 HTML 저장이나 인쇄를 누르면 끝이에요. 전부 이 기기에서만 만들어져요.'
        }
    },
    vi: {
        open: 'Hướng dẫn', title: 'Hướng dẫn từng bước', sub: 'Làm theo — nút bấm sẽ thực sự hoạt động',
        next: 'Tiếp', prev: 'Lùi', done: 'Xong', close: 'Đóng', step: 'Bước',
        g: {ai: 'Huấn luyện AI của bạn', data: 'Biểu đồ và dữ liệu', python: 'Lập trình Python', report: 'Tạo báo cáo học tập'},
        s: {
            aiS1: 'Tab Học AI giúp bạn dạy máy tính nhận biết đồ vật bằng webcam.',
            aiS2: 'Mở tab Học AI.',
            aiS3: 'Tạo một lớp (ví dụ "quả táo") rồi chụp khoảng 20 ảnh bằng webcam.',
            aiS4: 'Tạo thêm một lớp nữa rồi nhấn Huấn luyện. Chờ đến khi xong.',
            aiS5: 'Quay lại tab Mã lệnh, dùng các khối AI để phản ứng với kết quả nhận diện.',
            dataS1: 'Tiện ích Biểu đồ giúp bạn thu thập số liệu và vẽ đồ thị.',
            dataS2: 'Thêm "Biểu đồ dữ liệu" từ thư viện tiện ích.',
            dataS3: 'Dùng khối "thêm dữ liệu" để nhập nhãn và giá trị (ví dụ táo 5).',
            dataS4: 'Chạy khối "hiện biểu đồ" và đồ thị sẽ xuất hiện trên sân khấu.',
            pyS1: 'Tab Python chạy Python thật ngay trên trình duyệt.',
            pyS2: 'Mở tab Python.',
            pyS3: 'Gõ print("xin chào") rồi nhấn Run.',
            pyS4: 'Nhấn nút Python 3 để dùng các thư viện như pandas.',
            rpS1: 'Bạn có thể lưu những gì đã làm hôm nay thành báo cáo — rất hợp cho hồ sơ.',
            rpS2: 'Nhấn nút Báo cáo trên thanh trên cùng.',
            rpS3: 'Điền tên rồi Lưu HTML hoặc In. Mọi thứ chỉ được tạo trên thiết bị của bạn.'
        }
    },
    ja: {
        open: 'やってみる', title: 'ステップガイド', sub: 'ボタンをおすと、じっさいにそこへ行けます',
        next: 'つぎへ', prev: 'もどる', done: '完了', close: '閉じる', step: 'ステップ',
        g: {ai: '自分だけのAIを学習させる', data: 'データでグラフを作る', python: 'Pythonでコーディング', report: '学習レポートを作る'},
        s: {
            aiS1: 'AI学習タブでは、ウェブカメラを使ってコンピューターに物を見わける方法を教えられます。',
            aiS2: 'AI学習タブを開きましょう。',
            aiS3: 'クラスを1つ作って（例：「りんご」）、ウェブカメラで20枚くらい写真をとります。',
            aiS4: 'もう1つクラスを作ってから、学習ボタンをおします。おわるまで待ちましょう。',
            aiS5: 'コードタブにもどって、AIブロックで見わけた結果に反応させましょう。',
            dataS1: 'データグラフで数字を集めて、グラフをかくことができます。',
            dataS2: '拡張機能から「データグラフ」を追加します。',
            dataS3: '「データを追加」ブロックで項目と数字を入れます（例：りんご 5）。',
            dataS4: '「グラフを表示」ブロックを実行すると、ステージにグラフが出ます。',
            pyS1: 'Pythonタブでは、本物のPythonをブラウザでそのまま実行できます。',
            pyS2: 'Pythonタブを開きましょう。',
            pyS3: 'print("こんにちは") と書いて ▶ Run をおします。',
            pyS4: 'Python 3 ボタンをおすと、pandas などのライブラリも使えます。',
            rpS1: '今日やったことをレポートに保存できます。ポートフォリオにぴったり。',
            rpS2: '上のバーのレポートボタンをおしましょう。',
            rpS3: '名前を入れて、HTML保存か印刷をおせば完成。ぜんぶこの端末だけで作られます。'
        }
    },
    'zh-cn': {
        open: '跟着做', title: '分步指南', sub: '跟着做 — 按钮会真的带你过去',
        next: '下一步', prev: '上一步', done: '完成', close: '关闭', step: '步骤',
        g: {ai: '训练你自己的 AI', data: '用数据做图表', python: '用 Python 编程', report: '生成学习报告'},
        s: {
            aiS1: 'AI 学习标签页可以用摄像头教电脑认识物体。',
            aiS2: '打开 AI 学习标签页。',
            aiS3: '新建一个类别（例如"苹果"），用摄像头拍大约 20 张照片。',
            aiS4: '再建一个类别，然后点击训练。等它完成。',
            aiS5: '回到代码标签页，用 AI 积木对识别结果做出反应。',
            dataS1: '数据图表扩展可以收集数字并画出图表。',
            dataS2: '在扩展库中添加"数据图表"。',
            dataS3: '用"添加数据"积木填入名称和数值（例如 苹果 5）。',
            dataS4: '运行"显示图表"积木，舞台上就会出现图表。',
            pyS1: 'Python 标签页可以在浏览器里直接运行真正的 Python。',
            pyS2: '打开 Python 标签页。',
            pyS3: '输入 print("你好") 然后点击 Run。',
            pyS4: '点击 Python 3 按钮就能使用 pandas 等库。',
            rpS1: '你可以把今天做的内容保存成报告 — 很适合作品集。',
            rpS2: '点击顶部栏的报告按钮。',
            rpS3: '填上名字，然后保存 HTML 或打印即可。全部只在本设备生成。'
        }
    },
    'zh-tw': {
        open: '跟著做', title: '分步指南', sub: '跟著做 — 按鈕會真的帶你過去',
        next: '下一步', prev: '上一步', done: '完成', close: '關閉', step: '步驟',
        g: {ai: '訓練你自己的 AI', data: '用資料做圖表', python: '用 Python 程式設計', report: '產生學習報告'},
        s: {
            aiS1: 'AI 學習分頁可以用攝影機教電腦認識物體。',
            aiS2: '開啟 AI 學習分頁。',
            aiS3: '新增一個類別（例如「蘋果」），用攝影機拍大約 20 張照片。',
            aiS4: '再建一個類別，然後按下訓練。等它完成。',
            aiS5: '回到程式分頁，用 AI 積木對辨識結果做出反應。',
            dataS1: '資料圖表擴充可以蒐集數字並畫出圖表。',
            dataS2: '在擴充功能庫中加入「資料圖表」。',
            dataS3: '用「加入資料」積木填入名稱和數值（例如 蘋果 5）。',
            dataS4: '執行「顯示圖表」積木，舞台上就會出現圖表。',
            pyS1: 'Python 分頁可以在瀏覽器裡直接執行真正的 Python。',
            pyS2: '開啟 Python 分頁。',
            pyS3: '輸入 print("你好") 然後按 Run。',
            pyS4: '按下 Python 3 按鈕就能使用 pandas 等函式庫。',
            rpS1: '你可以把今天做的內容儲存成報告 — 很適合作品集。',
            rpS2: '按下頂端列的報告按鈕。',
            rpS3: '填上名字，然後儲存 HTML 或列印即可。全部只在本裝置產生。'
        }
    },
    de: {
        open: 'Anleitungen', title: 'Schritt-für-Schritt', sub: 'Mitmachen — die Buttons machen es wirklich',
        next: 'Weiter', prev: 'Zurück', done: 'Fertig', close: 'Schließen', step: 'Schritt',
        g: {ai: 'Eigene KI trainieren', data: 'Diagramme und Daten', python: 'Mit Python programmieren', report: 'Lernbericht erstellen'},
        s: {
            aiS1: 'Im KI-Lernen-Tab bringst du dem Computer mit der Webcam bei, Dinge zu erkennen.',
            aiS2: 'Öffne den KI-Lernen-Tab.',
            aiS3: 'Erstelle eine Klasse (z. B. "Apfel") und mach etwa 20 Fotos mit der Webcam.',
            aiS4: 'Erstelle noch eine Klasse und drücke dann Trainieren. Warte, bis es fertig ist.',
            aiS5: 'Zurück im Code-Tab reagierst du mit den KI-Blöcken auf das Erkannte.',
            dataS1: 'Mit der Daten-Diagramm-Erweiterung sammelst du Zahlen und zeichnest Diagramme.',
            dataS2: 'Füge "Daten-Diagramm" aus der Erweiterungsgalerie hinzu.',
            dataS3: 'Nutze den Block "Daten hinzufügen" für Bezeichnung und Wert (z. B. Apfel 5).',
            dataS4: 'Führe den Block "Diagramm zeigen" aus — das Diagramm erscheint auf der Bühne.',
            pyS1: 'Der Python-Tab führt echtes Python direkt im Browser aus.',
            pyS2: 'Öffne den Python-Tab.',
            pyS3: 'Schreibe print("hallo") und drücke Run.',
            pyS4: 'Mit dem Python-3-Button kannst du Bibliotheken wie pandas nutzen.',
            rpS1: 'Du kannst speichern, was du heute gemacht hast — ideal fürs Portfolio.',
            rpS2: 'Drücke den Bericht-Button in der oberen Leiste.',
            rpS3: 'Namen eintragen, dann HTML speichern oder drucken. Alles entsteht nur auf deinem Gerät.'
        }
    },
    es: {
        open: 'Guías', title: 'Guías paso a paso', sub: 'Sigue los pasos — los botones lo hacen de verdad',
        next: 'Siguiente', prev: 'Atrás', done: 'Listo', close: 'Cerrar', step: 'Paso',
        g: {ai: 'Entrena tu propia IA', data: 'Gráficos y datos', python: 'Programa con Python', report: 'Crear informe de aprendizaje'},
        s: {
            aiS1: 'La pestaña Aprendizaje IA te deja enseñar al ordenador a reconocer cosas con la webcam.',
            aiS2: 'Abre la pestaña Aprendizaje IA.',
            aiS3: 'Crea una clase (por ejemplo "manzana") y haz unas 20 fotos con la webcam.',
            aiS4: 'Crea otra clase más y pulsa Entrenar. Espera a que termine.',
            aiS5: 'Vuelve a Código y usa los bloques de IA para reaccionar a lo que reconoce.',
            dataS1: 'La extensión de gráficos te permite reunir números y dibujar gráficas.',
            dataS2: 'Añade "Gráfico de datos" desde la galería de extensiones.',
            dataS3: 'Usa el bloque "añadir dato" para poner una etiqueta y un valor (manzana 5).',
            dataS4: 'Ejecuta el bloque "mostrar gráfico" y aparecerá en el escenario.',
            pyS1: 'La pestaña Python ejecuta Python de verdad en el navegador.',
            pyS2: 'Abre la pestaña Python.',
            pyS3: 'Escribe print("hola") y pulsa Run.',
            pyS4: 'Pulsa el botón Python 3 para usar librerías como pandas.',
            rpS1: 'Puedes guardar lo que hiciste hoy como informe — ideal para tu portafolio.',
            rpS2: 'Pulsa el botón Informe en la barra superior.',
            rpS3: 'Pon tu nombre y luego Guardar HTML o Imprimir. Todo se crea solo en tu dispositivo.'
        }
    },
    fr: {
        open: 'Guides', title: 'Guides pas à pas', sub: 'Suis les étapes — les boutons le font vraiment',
        next: 'Suivant', prev: 'Retour', done: 'Terminé', close: 'Fermer', step: 'Étape',
        g: {ai: 'Entraîne ta propre IA', data: 'Graphiques et données', python: 'Programme en Python', report: 'Créer un rapport'},
        s: {
            aiS1: "L'onglet Apprentissage IA te permet d'apprendre à l'ordinateur à reconnaître des objets avec la webcam.",
            aiS2: "Ouvre l'onglet Apprentissage IA.",
            aiS3: 'Crée une classe (par exemple « pomme ») puis prends une vingtaine de photos avec la webcam.',
            aiS4: 'Crée une deuxième classe puis appuie sur Entraîner. Attends que ce soit fini.',
            aiS5: "De retour dans Code, utilise les blocs IA pour réagir à ce qui est reconnu.",
            dataS1: "L'extension Graphique permet de collecter des nombres et de tracer des graphiques.",
            dataS2: 'Ajoute « Graphique de données » depuis la galerie des extensions.',
            dataS3: 'Utilise le bloc « ajouter une donnée » pour saisir un nom et une valeur (pomme 5).',
            dataS4: 'Lance le bloc « afficher le graphique » : il apparaît sur la scène.',
            pyS1: "L'onglet Python exécute du vrai Python directement dans le navigateur.",
            pyS2: "Ouvre l'onglet Python.",
            pyS3: 'Écris print("bonjour") puis appuie sur Run.',
            pyS4: 'Appuie sur le bouton Python 3 pour utiliser des bibliothèques comme pandas.',
            rpS1: "Tu peux enregistrer ce que tu as fait aujourd'hui — parfait pour ton portfolio.",
            rpS2: 'Appuie sur le bouton Rapport dans la barre du haut.',
            rpS3: "Mets ton nom, puis Enregistrer HTML ou Imprimer. Tout est créé uniquement sur ton appareil."
        }
    },
    it: {
        open: 'Guide', title: 'Guide passo passo', sub: 'Segui i passi — i pulsanti lo fanno davvero',
        next: 'Avanti', prev: 'Indietro', done: 'Fatto', close: 'Chiudi', step: 'Passo',
        g: {ai: 'Addestra la tua AI', data: 'Grafici e dati', python: 'Programma con Python', report: 'Crea un report'},
        s: {
            aiS1: "La scheda Apprendimento AI ti fa insegnare al computer a riconoscere le cose con la webcam.",
            aiS2: 'Apri la scheda Apprendimento AI.',
            aiS3: 'Crea una classe (per esempio "mela") e scatta una ventina di foto con la webcam.',
            aiS4: 'Crea un\'altra classe e premi Addestra. Aspetta che finisca.',
            aiS5: 'Torna in Codice e usa i blocchi AI per reagire a ciò che riconosce.',
            dataS1: "L'estensione Grafico dati raccoglie numeri e disegna grafici.",
            dataS2: 'Aggiungi "Grafico dati" dalla galleria delle estensioni.',
            dataS3: 'Usa il blocco "aggiungi dato" per inserire etichetta e valore (mela 5).',
            dataS4: 'Esegui il blocco "mostra grafico": comparirà sullo stage.',
            pyS1: 'La scheda Python esegue Python vero direttamente nel browser.',
            pyS2: 'Apri la scheda Python.',
            pyS3: 'Scrivi print("ciao") e premi Run.',
            pyS4: 'Premi il pulsante Python 3 per usare librerie come pandas.',
            rpS1: 'Puoi salvare quello che hai fatto oggi come report — ottimo per il portfolio.',
            rpS2: 'Premi il pulsante Report nella barra in alto.',
            rpS3: 'Metti il tuo nome, poi Salva HTML o Stampa. Tutto viene creato solo sul tuo dispositivo.'
        }
    },
    ru: {
        open: 'Инструкции', title: 'Пошаговые инструкции', sub: 'Следуй шагам — кнопки действительно работают',
        next: 'Далее', prev: 'Назад', done: 'Готово', close: 'Закрыть', step: 'Шаг',
        g: {ai: 'Обучи свой ИИ', data: 'Графики и данные', python: 'Программируй на Python', report: 'Создать отчёт'},
        s: {
            aiS1: 'Вкладка «Обучение ИИ» позволяет научить компьютер узнавать предметы через веб-камеру.',
            aiS2: 'Открой вкладку «Обучение ИИ».',
            aiS3: 'Создай класс (например «яблоко») и сделай около 20 снимков веб-камерой.',
            aiS4: 'Создай ещё один класс и нажми «Обучить». Дождись завершения.',
            aiS5: 'Вернись во вкладку «Код» и реагируй на распознанное с помощью блоков ИИ.',
            dataS1: 'Расширение «График данных» собирает числа и рисует графики.',
            dataS2: 'Добавь «График данных» из галереи расширений.',
            dataS3: 'Блоком «добавить данные» введи название и значение (яблоко 5).',
            dataS4: 'Запусти блок «показать график» — график появится на сцене.',
            pyS1: 'Вкладка Python запускает настоящий Python прямо в браузере.',
            pyS2: 'Открой вкладку Python.',
            pyS3: 'Напиши print("привет") и нажми Run.',
            pyS4: 'Нажми кнопку Python 3, чтобы использовать библиотеки вроде pandas.',
            rpS1: 'Можно сохранить то, что ты сделал сегодня, как отчёт — отлично для портфолио.',
            rpS2: 'Нажми кнопку «Отчёт» на верхней панели.',
            rpS3: 'Впиши имя, затем «Сохранить HTML» или «Печать». Всё создаётся только на твоём устройстве.'
        }
    }
};

/** en 단계 텍스트 폴백 채우기(ko 외 언어는 en 사용) */
GUIDE_L.en.s = Object.assign({
    dataS1: 'The Data Chart extension lets you collect numbers and draw graphs.',
    dataS2: 'Add "Data Chart" from the extension gallery.',
    dataS3: 'Use the "add data" block to put in a label and a value (like apple 5).',
    dataS4: 'Run the "show chart" block and a graph appears on the stage.',
    pyS1: 'The Python tab runs real Python right in your browser.',
    pyS2: 'Open the Python tab.',
    pyS3: 'Type print("hello") and press Run.',
    pyS4: 'Press the Python 3 button to use libraries like pandas.',
    rpS1: 'You can save what you did today as a report — great for a portfolio.',
    rpS2: 'Press the Report button in the top bar.',
    rpS3: 'Fill in your name, then Save HTML or Print. Everything is made on your device only.'
}, GUIDE_L.en.s);

/** 로케일 → 가이드 문구 (없으면 en) */
export const guideL = loc => {
    let l = loc || 'en';
    if (l === 'ja-Hira') l = 'ja';
    const base = l.split('-')[0];
    return GUIDE_L[l] || GUIDE_L[base] || GUIDE_L.en;
};

/** 단계 텍스트 (해당 언어에 없으면 en 폴백) */
export const guideStep = (loc, key) => {
    const L = guideL(loc);
    return (L.s && L.s[key]) || GUIDE_L.en.s[key] || '';
};
